import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import UndoIcon from "@mui/icons-material/Undo";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getAwaitingInputSurfaceStyles } from "@/shared/utils/awaitingInputStyles";
import type { ReviewGap } from "../types";

interface ClarificationPanelProps {
  gaps: ReviewGap[];
  onResolveGap: (id: string, resolution: string) => void;
  /** Strike the question out — it doesn't apply to this incident. */
  onDismissGap: (id: string) => void;
  /** Put a struck-out question back, with whatever was typed in it. */
  onRestoreGap: (id: string) => void;
  disabled?: boolean;
}

// ── Gap Item ───────────────────────────────────────────

function GapItem({
  gap,
  onResolve,
  onDismiss,
  onRestore,
  disabled,
}: {
  gap: ReviewGap;
  onResolve: (id: string, resolution: string) => void;
  onDismiss: (id: string) => void;
  onRestore: (id: string) => void;
  disabled?: boolean;
}) {
  const dismissed = Boolean(gap.dismissed);

  return (
    <Box
      sx={(theme) => {
        const awaitingInputStyles = getAwaitingInputSurfaceStyles(theme);
        return {
          py: 1.25,
          px: 1.5,
          borderRadius: 2,
          // A dismissed question drops out of the awaiting-input palette —
          // it's no longer asking for anything.
          backgroundColor: dismissed
            ? "transparent"
            : awaitingInputStyles.backgroundColor,
          border: `1px ${dismissed ? "dashed" : "solid"} ${
            dismissed ? theme.palette.divider : awaitingInputStyles.borderColor
          }`,
        };
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <WarningAmberIcon
          fontSize="small"
          sx={(theme) => ({
            mt: 0.25,
            color: dismissed
              ? theme.palette.text.disabled
              : getAwaitingInputSurfaceStyles(theme).accentColor,
          })}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              ...(dismissed
                ? { textDecoration: "line-through", color: "text.disabled" }
                : {}),
            }}
          >
            {gap.description}
          </Typography>
          <Chip
            label={gap.category}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
          />

          {dismissed ? (
            // Answer inputs are gone, but the item stays in place so the
            // officer can find it and undo. Nothing has been sent yet — the
            // dismissal travels with the next approval or change request.
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography variant="caption" color="text.secondary">
                Dismissed — won't be asked again.
              </Typography>
              <Button
                size="small"
                startIcon={<UndoIcon fontSize="small" />}
                onClick={() => onRestore(gap.id)}
                disabled={disabled}
              >
                Undo
              </Button>
            </Stack>
          ) : gap.type === "multiple-options" && gap.options?.length ? (
            <RadioGroup
              value={gap.resolution ?? ""}
              onChange={(e) => onResolve(gap.id, e.target.value)}
              sx={{ mt: 1 }}
            >
              {gap.options.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio size="small" disabled={disabled} />}
                  label={<Typography variant="body2">{option}</Typography>}
                />
              ))}
            </RadioGroup>
          ) : (
            <TextField
              size="small"
              placeholder={gap.type === "missing" ? "Enter value..." : "Clarify..."}
              value={gap.resolution ?? ""}
              onChange={(e) => onResolve(gap.id, e.target.value)}
              fullWidth
              disabled={disabled}
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        {!dismissed && (
          <Tooltip title="Doesn't apply — dismiss this question">
            {/* Wrapper so the tooltip still has an event target once the
                button is disabled mid-submit (MUI requirement). */}
            <span>
              <IconButton
                size="small"
                onClick={() => onDismiss(gap.id)}
                disabled={disabled}
                aria-label={`Dismiss question: ${gap.description}`}
              >
                <DoNotDisturbAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

// ── Panel ──────────────────────────────────────────────

export const ClarificationPanel: React.FC<ClarificationPanelProps> = ({
  gaps,
  onResolveGap,
  onDismissGap,
  onRestoreGap,
  disabled,
}) => {
  if (gaps.length === 0) {
    return (
      <Alert severity="success">
        The AI Assistant has no outstanding questions for this First Draft.
      </Alert>
    );
  }

  const openCount = gaps.filter((gap) => !gap.dismissed).length;
  const dismissedCount = gaps.length - openCount;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography
          variant="overline"
          component="p"
          sx={(theme) => ({
            fontWeight: 700,
            color:
              openCount > 0
                ? getAwaitingInputSurfaceStyles(theme).accentColor
                : theme.palette.text.secondary,
            letterSpacing: 1.5,
          })}
        >
          Awaiting Clarification ({openCount})
        </Typography>
        {dismissedCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {dismissedCount} dismissed — sent with your next approval or change
            request, and never asked again.
          </Typography>
        )}
      </Box>
      {/* Dismissed questions keep their position rather than moving to the
          bottom, so the item the officer just struck out stays where they
          were looking. */}
      {gaps.map((gap) => (
        <GapItem
          key={gap.id}
          gap={gap}
          onResolve={onResolveGap}
          onDismiss={onDismissGap}
          onRestore={onRestoreGap}
          disabled={disabled}
        />
      ))}
    </Stack>
  );
};
